import React, { useId } from "react";

import { type SxProps, type Theme, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { type LucideIcon, Sparkles } from "lucide-react";

import { IgPaper } from "./IgPaper";
import sparklesUrl from "./assets/sparkles.svg";

/** Frame drawn around the glyph. */
export type IgAiIconSurface = "none" | "square" | "square-soft" | "round";
/** How the glyph itself is painted. */
export type IgAiIconTone = "color" | "mono";

export interface IgAiIconProps {
  /**
   * The lucide glyph to render. Defaults to `Sparkles`, which renders the
   * branded `sparkles.svg` silhouette tinted with the AI gradient (or the solid
   * text color in `tone="mono"`) instead of a lucide glyph.
   */
  icon?: LucideIcon;
  /**
   * Frame around the glyph:
   * - `"none"` — bare glyph on a transparent background.
   * - `"square"` (default) — rounded square framed by the AI gradient stroke
   *   (the `IgPaper` `variant="ai"` surface), for a boxed-icon look.
   * - `"square-soft"` — a tinted rounded square: the same soft AI wash as
   *   `"round"`, boxed into a 10px-radius square with no gradient frame.
   * - `"round"` — a tinted circular disc, the avatar look used in turn/chat
   *   streams.
   */
  surface?: IgAiIconSurface;
  /**
   * Glyph paint:
   * - `"color"` (default) — stroked with the iridescent AI gradient.
   * - `"mono"` — solid text color (black), no gradient.
   */
  tone?: IgAiIconTone;
  /**
   * Edge length (px). For the boxed surfaces this is the frame size; for
   * `surface="none"` it is the glyph size.
   */
  size?: number;
  /** Inner glyph size (px) for boxed surfaces. Defaults to 5/9 of the frame. */
  glyphSize?: number;
  sx?: SxProps<Theme>;
  /** Play a one-shot gradient "shine" sweep across the frame on hover. */
  shine?: boolean;
  /** Accessible label; when omitted the mark is decorative (`aria-hidden`). */
  "aria-label"?: string;
}

const DEFAULT_SIZE = 48;

type GlyphProps = {
  icon: LucideIcon;
  tone: IgAiIconTone;
  size: number;
  "aria-label"?: string;
};

/**
 * Renders a single glyph at `size`px.
 *
 * The `Sparkles` mark is special-cased: it renders the branded `sparkles.svg`
 * silhouette tinted by the component through a CSS mask — the AI gradient in
 * `tone="color"`, the solid text color in `tone="mono"` — so the asset stays the
 * single source for the sparkle shape. (The mask reads only the asset's alpha,
 * so its own stroke color is irrelevant.)
 *
 * Every other glyph comes straight from lucide: `tone="mono"` paints a solid
 * glyph, while `tone="color"` strokes an `<svg>` whose child paths inherit
 * `stroke` — a CSS gradient can't reach them, so the stroke must point at an SVG
 * gradient paint server (`url(#id)`). Each instance carries its own zero-size
 * `<defs>` keyed by `useId()`, so multiple icons never collide on the id.
 */
const Glyph = ({
  icon: Icon,
  tone,
  size,
  "aria-label": ariaLabel,
}: GlyphProps) => {
  const theme = useTheme();
  const rawId = useId();
  const a11y = {
    role: ariaLabel ? "img" : undefined,
    "aria-label": ariaLabel,
    "aria-hidden": ariaLabel ? undefined : true,
  } as const;

  // The branded Sparkles mark is the sparkles.svg silhouette, tinted here via
  // CSS mask: the AI gradient for `tone="color"`, the flat text color for
  // `tone="mono"`. Only the asset's alpha drives the mask, so its stroke color
  // doesn't matter.
  if (Icon === Sparkles) {
    const mask = `url("${sparklesUrl}") center / contain no-repeat`;
    return (
      <span
        {...a11y}
        style={{
          display: "inline-block",
          flexShrink: 0,
          width: size,
          height: size,
          background:
            tone === "mono"
              ? theme.palette.text.primary
              : theme.igAi.gradient.icon,
          WebkitMask: mask,
          mask,
        }}
      />
    );
  }

  if (tone === "mono") {
    return <Icon size={size} color={theme.palette.text.primary} {...a11y} />;
  }

  // ':' from useId is invalid inside url(#…); strip it.
  const gradientId = `ig-ai-icon-${rawId.replace(/:/g, "")}`;
  const paint = `url(#${gradientId})`;

  return (
    <>
      <svg width={0} height={0} aria-hidden style={{ position: "absolute" }}>
        <defs>
          {/* Mapped to lucide's 0 0 24 24 viewBox in userSpaceOnUse (not the
              default objectBoundingBox): icons like MessageSquareMore draw
              their dots as zero-length lines, and an objectBoundingBox gradient
              is spec-ignored on a zero-area bbox — which silently drops them.
              x1/y1 → x2/y2 encodes the 195° angle across the 24-unit box. */}
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="15.1"
            y1="0.4"
            x2="8.9"
            y2="23.6"
          >
            {theme.igAi.gradient.iconStops.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Icon size={size} stroke={paint} {...a11y} />
    </>
  );
};

/**
 * The Intelligems AI glyph — the default AI affordance used in banners,
 * buttons, alerts and chat streams. Defaults to the branded `sparkles.svg` mark
 * (mask-tinted with the AI gradient) inside a square gradient-bordered frame;
 * the `icon`, `surface` and `tone` props cover the other presentations:
 *
 *   <IgAiIcon />                              // square box, gradient sparkle
 *   <IgAiIcon surface="none" />               // bare colorful sparkle
 *   <IgAiIcon surface="none" tone="mono" />   // bare black sparkle
 *   <IgAiIcon icon={SearchCheck} tone="mono" />          // black glyph, boxed
 *   <IgAiIcon surface="square-soft" />        // soft-tinted square, no frame
 *   <IgAiIcon icon={MessageSquareMore} surface="round" shine />  // chat avatar
 */
export const IgAiIcon = ({
  icon = Sparkles,
  surface = "square-soft",
  tone = "color",
  size = DEFAULT_SIZE,
  glyphSize,
  sx,
  shine,
  "aria-label": ariaLabel,
}: IgAiIconProps) => {
  const theme = useTheme();

  // Bare glyph: `size` IS the glyph; there is no frame to shine across.
  if (surface === "none") {
    return (
      <Glyph
        icon={icon}
        tone={tone}
        size={glyphSize ?? size}
        aria-label={ariaLabel}
      />
    );
  }

  const glyph = (
    <Glyph
      icon={icon}
      tone={tone}
      size={glyphSize ?? Math.round((size * 5) / 9)}
      aria-label={ariaLabel}
    />
  );

  const shineSx = shine && {
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      background: theme.igAi.shine.band,
      transform: "translateX(-100%)",
      pointerEvents: "none",
    },
    "&:hover::after": {
      animation: `${theme.igAi.shine.sweep} 0.8s ease-out`,
    },
  };

  // Filled surfaces: the strong AI wash composited over white, shaped as a
  // circular disc (`"round"`, the turn/chat avatar) or a 10px-radius rounded
  // square (`"square-soft"`). Both differ from `"square"` in that they carry no
  // gradient frame — the tint *is* the surface.
  if (surface === "round" || surface === "square-soft") {
    return (
      <Stack
        sx={[
          {
            width: size,
            height: size,
            minWidth: size,
            flexShrink: 0,
            background: `${theme.igAi.surface.strong}, white`,
            backgroundBlendMode: "multiply, normal",
            borderRadius: surface === "round" ? "100%" : "10px",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            ...shineSx,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {glyph}
      </Stack>
    );
  }

  // surface === "square": rounded square framed by the AI gradient stroke
  // (the IgPaper `variant="ai"` surface).
  return (
    <IgPaper
      variant="ai"
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: size,
          height: size,
          minWidth: size,
          p: 0,
          borderRadius: "10px",
          ...shineSx,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {glyph}
    </IgPaper>
  );
};
